import { useState } from "react";
import { IonInput, IonTextarea, IonButton, IonIcon } from "@ionic/react";
import { arrowUpOutline, arrowDownOutline } from "ionicons/icons";
import EditSheet from "@/components/EditSheet";
import { FLOAT } from "@/components/SettingsRows";
import { userHeaders } from "@/utils/userAuth";
import { toast } from "@/utils/toast";
import { roleColor } from "@/utils/signDocFields";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Send an uploaded document for signature: one name + email per signer
// role, in signing order (the arrows reorder it), and an optional note. The
// first signer gets the link now; each next one when the one before signs.
// Sending your own document is unlimited (no downloads). Roles with no
// fields of their own are left out.
export default function SendSignDocSheet({ doc, onClose, onSent }) {
  const fields = doc.fields || [];
  const [rows, setRows] = useState(() => (doc.roles || [])
    .filter((r) => fields.some((f) => f.role === r.id))
    .map((r) => ({ roleId: r.id, label: r.label, name: "", email: "" })));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (i, k, v) => setRows((list) => list.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const move = (i, d) => setRows((list) => {
    const j = i + d;
    if (j < 0 || j >= list.length) return list;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  const send = async () => {
    if (!rows.length) { toast.error("Place a field for at least one signer first"); return; }
    const bad = rows.find((r) => !/^\S+@\S+\.\S+$/.test(r.email.trim()));
    if (bad) { toast.error(`Enter an email for ${bad.label}`); return; }
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/sign-documents/${encodeURIComponent(doc.id)}/send`, {
        method: "POST", headers: userHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ signers: rows.map((r) => ({ roleId: r.roleId, name: r.name.trim(), email: r.email.trim() })), note: note.trim() }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not send");
      toast.success(rows.length > 1 ? `Sent to ${rows[0].email.trim()}, then ${rows.length - 1} more in order` : `Sent to ${rows[0].email.trim()}`);
      onSent && onSent(d);
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  return (
    <EditSheet title={`Send ${doc.name || "document"}`} onClose={onClose} onSave={send} saving={busy} saveLabel="Send" className="sdoc-send-sheet">
      {rows.length === 0 && (
        <p style={{ margin: 0, color: "var(--ion-color-medium)", fontSize: "0.9rem" }}>No signer has a field yet. Place fields for a signer in the editor first.</p>
      )}
      {rows.map((r, i) => {
        const color = roleColor(doc.roles, r.roleId);
        return (
          <div key={r.roleId} className="sdoc-send-row" style={{ borderLeft: `4px solid ${color}` }}>
            <div className="sdoc-send-head">
              <span className="sdoc-send-order" style={{ background: color }}>{i + 1}</span>
              <strong>{r.label}</strong>
              <span style={{ flex: 1 }} />
              {rows.length > 1 && (
                <>
                  <IonButton fill="clear" size="small" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Sign earlier" title="Sign earlier">
                    <IonIcon slot="icon-only" icon={arrowUpOutline} />
                  </IonButton>
                  <IonButton fill="clear" size="small" disabled={i === rows.length - 1} onClick={() => move(i, 1)} aria-label="Sign later" title="Sign later">
                    <IonIcon slot="icon-only" icon={arrowDownOutline} />
                  </IonButton>
                </>
              )}
            </div>
            <IonInput {...FLOAT} label="Name" value={r.name} onIonInput={(e) => set(i, "name", e.detail.value ?? "")} />
            <IonInput {...FLOAT} label="Email" type="email" inputmode="email" value={r.email} onIonInput={(e) => set(i, "email", e.detail.value ?? "")} style={{ marginTop: 10 }} />
          </div>
        );
      })}
      <IonTextarea {...FLOAT} label="Note to the signers (optional)" autoGrow rows={2} value={note} onIonInput={(e) => setNote(e.detail.value ?? "")} />
      <p style={{ margin: 0, fontSize: "0.8rem", color: "var(--ion-color-medium)" }}>
        {rows.length > 1 ? "Signers get the document one at a time, in this order. " : ""}Sending your own documents is unlimited and uses no downloads.
      </p>
    </EditSheet>
  );
}
